import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import { generateEventSchema } from './schemaBuilder';

export const UiNotificationMethods = [
  'show-banner',
  'update-banner',
  'hide-banner',

  'show-modal',
  'update-modal',
  'hide-modal',
  'show-ui',
  'update-ui',
  'hide-ui',
] as const;
export type UiNotificationMethod = (typeof UiNotificationMethods)[number];

export const UiRequestMethods = [
  'show-retry-popup',
  'hide-retry-popup',
] as const;
export type UiRequestMethod = (typeof UiRequestMethods)[number];

export type UiMethod = UiNotificationMethod | UiRequestMethod;

const namespace: InfinexMessageNamespace = 'infinex-ui';

/* ─── Payload shapes ───────────────────── */

// Banner notifications
export const showBannerRequestSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    message: z.string(),
  })
  .strict();

export const updateBannerRequestSchema = z.unknown();
export const hideBannerRequestSchema = z.unknown();

// Modal notifications
export const showModalRequestSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
  })
  .strict();

export const updateModalRequestSchema = z.unknown();
export const hideModalRequestSchema = z.unknown();

// Retry‐popup (request + response)
export const showRetryPopupRequestSchema = z
  .object({ url: z.string() })
  .strict();

export const showRetryPopupResponseSchema = z
  .object({
    elementId: z.string(),
    openEvent: z.string(),
    cancelEvent: z.string(),
  })
  .strict();

export const hideRetryPopupRequestSchema = z
  .object({ elementId: z.string() })
  .strict();

export const hideRetryPopupResponseSchema = z.null();

// General SDK notifications (notification‐only)
export const showNotificationSchema = z
  .object({
    manager: z.string(),
    html: z.string(),
  })
  .strict();

export const updateNotificationSchema = z
  .object({
    manager: z.string(),
    args: z.string(),
  })
  .strict();

export const hideNotificationSchema = z
  .object({
    manager: z.string(),
    args: z.string(),
  })
  .strict();

export const showBannerSchema = generateEventSchema({
  namespace,
  method: 'show-banner',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: showBannerRequestSchema,
});

export const updateBannerSchema = generateEventSchema({
  namespace,
  method: 'update-banner',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: updateBannerRequestSchema,
});

export const hideBannerSchema = generateEventSchema({
  namespace,
  method: 'hide-banner',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: hideBannerRequestSchema,
});

export const showModalSchema = generateEventSchema({
  namespace,
  method: 'show-modal',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: showModalRequestSchema,
});

export const updateModalSchema = generateEventSchema({
  namespace,
  method: 'update-modal',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: updateModalRequestSchema,
});

export const hideModalSchema = generateEventSchema({
  namespace,
  method: 'hide-modal',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: hideModalRequestSchema,
});

export const showRetryPopupSchema = generateEventSchema({
  namespace,
  method: 'show-retry-popup',

  requestData: showRetryPopupRequestSchema,
  responseData: showRetryPopupResponseSchema,
  notificationData: z.never(),
});

export const hideRetryPopupSchema = generateEventSchema({
  namespace,
  method: 'hide-retry-popup',

  requestData: hideRetryPopupRequestSchema,
  responseData: hideRetryPopupResponseSchema,
  notificationData: z.never(),
});

export const showNotificationEventSchema = generateEventSchema({
  namespace,
  method: 'show-ui',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: showNotificationSchema,
});

export const updateNotificationEventSchema = generateEventSchema({
  namespace,
  method: 'update-ui',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: updateNotificationSchema,
});

export const hideNotificationEventSchema = generateEventSchema({
  namespace,
  method: 'hide-ui',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: hideNotificationSchema,
});

/* ─────────────────── Aggregated schema arrays ─────────────────── */
export const infinexUiRequestParamSchemas = [
  showRetryPopupSchema.requestParamsSchema,
  hideRetryPopupSchema.requestParamsSchema,
] as const;

export const infinexUiResponseParamSchemas = [
  showRetryPopupSchema.responseParamsSchema,
  hideRetryPopupSchema.responseParamsSchema,
] as const;

export const infinexUiNotificationParamSchemas = [
  showBannerSchema.notificationParamsSchema,
  updateBannerSchema.notificationParamsSchema,
  hideBannerSchema.notificationParamsSchema,

  showModalSchema.notificationParamsSchema,
  updateModalSchema.notificationParamsSchema,
  hideModalSchema.notificationParamsSchema,

  showNotificationEventSchema.notificationParamsSchema,
  updateNotificationEventSchema.notificationParamsSchema,
  hideNotificationEventSchema.notificationParamsSchema,
] as const;

export const infinexUiRequestEventSchemas = [
  showRetryPopupSchema.requestSchema,
  hideRetryPopupSchema.requestSchema,
] as const;

export const infinexUiResponseEventSchemas = [
  showRetryPopupSchema.responseSchema,
  hideRetryPopupSchema.responseSchema,
] as const;

export const infinexUiNotificationEventSchemas = [
  showBannerSchema.notificationSchema,
  updateBannerSchema.notificationSchema,
  hideBannerSchema.notificationSchema,

  showModalSchema.notificationSchema,
  updateModalSchema.notificationSchema,
  hideModalSchema.notificationSchema,

  showNotificationEventSchema.notificationSchema,
  updateNotificationEventSchema.notificationSchema,
  hideNotificationEventSchema.notificationSchema,
] as const;
